import { Component, computed, DestroyRef, inject, input, model } from '@angular/core';
import { ChitinButtonComponent } from '@shared/components/chitin/atoms/button/button.component';
import { DaterangePickerComponent } from '@shared/components/daterange-picker/daterange-picker.component';
import { DateRange as MsgDateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DATERANGE_PRESET_TO_PRESET_ID_WITH_MODIFIER } from '@shared/models/date-range-preset';
import { Preset } from '@shared/components/daterange-picker/daterange-picker-const';

@Component({
  selector: 'app-button-daterange-picker',
  templateUrl: 'button-daterange-picker.component.html',
  styleUrl: './button-daterange-picker.component.scss',
  imports: [ChitinButtonComponent, DaterangePickerComponent, NgbPopoverModule],
})
export class ButtonDaterangePickerComponent {
  private readonly destroyRef = inject(DestroyRef);

  showTimezoneSelector = input(false);
  dateFormat = input<string>('MMM D, YYYY');
  isLoading = input(false);
  isDisabled = input(false);
  minDate = input<Date | Dayjs | string>();
  maxDate = input<Date | Dayjs | string>();
  size = input<'xs' | 'small' | 'medium' | 'large'>('medium');
  placeholder = input<string>('Select date');
  appendTo = input<'body'>();

  constructor() {
    toObservable(this.dateRange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newDateRange => {
        if (newDateRange?.preset) {
          const mappedPreset = DATERANGE_PRESET_TO_PRESET_ID_WITH_MODIFIER[newDateRange.preset];
          if (mappedPreset) {
            this.selectPreset(mappedPreset);
          }
        }
      });
  }

  protected dateRange = model<MsgDateRange>();

  protected dateRangeFormatted = computed(() => {
    const dateRange = this.dateRange();
    if (!dateRange) return null;
    const from = dayjs(dateRange.from);
    const to = dayjs(dateRange.to);
    if (from.date() === to.date()) {
      return `${from.format(this.dateFormat())}`;
    }
    return `${from.format(this.dateFormat())} - ${to.format(this.dateFormat())}`;
  });

  protected selectPreset(preset: Preset): void {
    if (!preset.modifier) return; // Nothing to logically select
    dayjs.extend(quarterOfYear);
    const now = dayjs();
    const from = now
      .subtract(preset.id === 'last' ? 1 : 0, preset.modifier as ManipulateType)
      .startOf(preset.modifier)
      .toDate();
    let to: Date;
    if (preset.id === 'toDate') {
      to = now.endOf('day').toDate();
    } else {
      to = now
        .subtract(preset.id === 'last' ? 1 : 0, preset.modifier as ManipulateType)
        .endOf(preset.modifier)
        .toDate();
    }
    this.dateRange.set({ from, to }); // Stripping preset to avoid infinite loop
  }
}
